import { LineChart, Line, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ComposedChart, Bar, ReferenceLine } from 'recharts';
import { Card } from './ui';

const usd = (v: number) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(v);



/**
 * Multi-série (ex.: Abort Reasons Over Time). Cada série vira uma linha.
 * A legenda é própria (chips abaixo do gráfico) em vez da <Legend> do Recharts:
 * com muitas séries numa coluna estreita ela comeria metade da área de plot.
 */
export function MultiLineCard({ title, data, series, height = 300 }: {
  title: string;
  data: Array<Record<string, string | number>>;
  series: { key: string; name: string; color: string }[];
  height?: number;
}) {
  return (
    <Card className="p-4 h-full flex flex-col">
      <div className="text-sm font-medium text-slate-600 mb-3">{title}</div>
      <div style={{ height }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--chart-grid)" vertical={false} />
            <XAxis dataKey="x" tick={{ fontSize: 11, fill: 'var(--chart-tick)' }} tickLine={false} axisLine={{ stroke: 'var(--chart-axis)' }} interval="preserveStartEnd" minTickGap={40} />
            <YAxis tick={{ fontSize: 11, fill: 'var(--chart-tick)' }} tickLine={false} axisLine={false} width={44}
              tickFormatter={(v) => (v >= 1000 ? `${(v / 1000).toFixed(0)}k` : String(v))} />
            <Tooltip contentStyle={{ borderRadius: 8, border: '1px solid var(--chart-tooltip-border)', backgroundColor: 'var(--chart-tooltip-bg)', color: 'var(--chart-tooltip-text)', fontSize: 11 }} labelStyle={{ color: 'var(--chart-tooltip-text)', fontWeight: 600 }} itemSorter={(i) => -(i.value as number)} />
            {series.map((s) => (
              <Line key={s.key} name={s.name} type="monotone" dataKey={s.key} stroke={s.color} strokeWidth={1.5} dot={{ r: 1.5 }} activeDot={{ r: 4 }} />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
      <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 max-h-20 overflow-y-auto">
        {series.map((s) => (
          <span key={s.key} className="inline-flex items-center gap-1 text-[10px] text-slate-500" title={s.name}>
            <span className="inline-block w-2.5 h-0.5 rounded" style={{ backgroundColor: s.color }} />
            <span className="truncate max-w-[150px]">{s.name}</span>
          </span>
        ))}
      </div>
    </Card>
  );
}

/** Área simples (ex.: AWS Daily Cost). */
export function AreaCard({ title, data, color = '#29abe2', format }: { title: string; data: { x: string; y: number }[]; color?: string; format?: (v: number) => string }) {
  const fmt = format || ((v: number) => `$${v.toFixed(0)}`);
  return (
    <Card className="p-4">
      <div className="text-sm font-semibold text-ink-900 mb-3">{title}</div>
      <div style={{ height: 260 }}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="awsFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={color} stopOpacity={0.85} />
                <stop offset="100%" stopColor={color} stopOpacity={0.35} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--chart-grid)" vertical={false} />
            <XAxis dataKey="x" tick={{ fontSize: 11, fill: 'var(--chart-tick)' }} tickLine={false} axisLine={{ stroke: 'var(--chart-axis)' }} interval="preserveStartEnd" />
            <YAxis tick={{ fontSize: 11, fill: 'var(--chart-tick)' }} tickLine={false} axisLine={false} width={56} tickFormatter={fmt} />
            <Tooltip contentStyle={{ borderRadius: 8, border: '1px solid var(--chart-tooltip-border)', backgroundColor: 'var(--chart-tooltip-bg)', color: 'var(--chart-tooltip-text)', fontSize: 12 }} labelStyle={{ color: 'var(--chart-tooltip-text)', fontWeight: 600 }} formatter={(v: number) => [fmt(v), title]} />
            <Area type="linear" name={title} dataKey="y" stroke={color} strokeWidth={2} fill="url(#awsFill)" dot={{ r: 2.5, fill: '#fff', stroke: color, strokeWidth: 1.5 }} activeDot={{ r: 5 }} />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </Card>
  );
}

/** Cash Flow: barras = caixa realizado (paid), linhas finas = competência (accrued). */
export function CashFlowChart({ data }: { data: Array<{ m: string; invoice_paid: number; aws_paid: number; llm_paid?: number; invoice_issued: number; aws_incurred: number; net_cash: number }> }) {
  return (
    <div style={{ height: 340 }}>
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={data} margin={{ top: 10, right: 15, left: 5, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--chart-grid)" vertical={false} />
          <XAxis dataKey="m" tick={{ fontSize: 11, fill: 'var(--chart-tick)' }} tickLine={false} axisLine={{ stroke: 'var(--chart-axis)' }} />
          <YAxis tick={{ fontSize: 11, fill: 'var(--chart-tick)' }} tickLine={false} axisLine={false} width={58}
            tickFormatter={(v) => (Math.abs(v) >= 1000 ? `$${(v / 1000).toFixed(0)}k` : `$${v}`)} />
          <Tooltip contentStyle={{ borderRadius: 8, border: '1px solid var(--chart-tooltip-border)', backgroundColor: 'var(--chart-tooltip-bg)', color: 'var(--chart-tooltip-text)', fontSize: 12 }} labelStyle={{ color: 'var(--chart-tooltip-text)', fontWeight: 600 }} formatter={(v: number, name: string) => [usd(v), name]} />
          <Legend wrapperStyle={{ fontSize: 11 }} />
          <ReferenceLine y={0} stroke="var(--chart-axis)" />
          <Bar dataKey="invoice_paid" name="Invoice Paid (cash in)" fill="#27ae60" radius={[4, 4, 0, 0]} />
          <Bar dataKey="aws_paid" name="AWS Paid (cash out)" fill="#c0392b" radius={[4, 4, 0, 0]} />
          <Bar dataKey="llm_paid" name="LLM Paid (cash out)" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
          <Line dataKey="invoice_issued" name="Invoice Issued (accrued)" stroke="#95c8a4" strokeWidth={1} dot={false} type="monotone" />
          <Line dataKey="aws_incurred" name="AWS Incurred (accrued)" stroke="#e0a4a4" strokeWidth={1} dot={false} type="monotone" />
          <Line dataKey="net_cash" name="Net Cash" stroke="var(--series-ink)" strokeWidth={2} dot={{ r: 3 }} type="monotone" />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

export interface Point { x: string; y: number }
export interface DualPoint { x: string; a: number; b: number }

/** Gráfico com duas séries (ex.: Clicks + Cost). */
export function DualLineCard({ title, data, aName, bName, aColor, bColor }: { title: string; data: DualPoint[]; aName: string; bName: string; aColor: string; bColor: string }) {
  return (
    <Card className="p-5">
      <div className="text-sm font-semibold text-ink-900 mb-3">{title}</div>
      <div style={{ height: 300 }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 5, right: 15, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--chart-grid)" vertical={false} />
            <XAxis dataKey="x" tick={{ fontSize: 11, fill: 'var(--chart-tick)' }} tickLine={false} axisLine={{ stroke: 'var(--chart-axis)' }} />
            <YAxis yAxisId="l" tick={{ fontSize: 11, fill: 'var(--chart-tick)' }} tickLine={false} axisLine={false} width={48} tickFormatter={(v) => (v >= 1000 ? `${(v / 1000).toFixed(0)}k` : String(v))} />
            <Tooltip contentStyle={{ borderRadius: 8, border: '1px solid var(--chart-tooltip-border)', backgroundColor: 'var(--chart-tooltip-bg)', color: 'var(--chart-tooltip-text)', fontSize: 12 }} labelStyle={{ color: 'var(--chart-tooltip-text)', fontWeight: 600 }} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Line yAxisId="l" name={aName} type="monotone" dataKey="a" stroke={aColor} strokeWidth={2} dot={{ r: 2 }} />
            <Line yAxisId="l" name={bName} type="monotone" dataKey="b" stroke={bColor} strokeWidth={2} dot={{ r: 2 }} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </Card>
  );
}

/** Card com um gráfico de linha simples (usado nos daily/real-time). */
export function LineCard({ title, data, color, format }: { title: string; data: Point[]; color: string; format?: (v: number) => string }) {
  return (
    <Card className="p-4 h-full">
      <div className="text-sm font-medium text-slate-600 mb-3">{title}</div>
      <div style={{ height: 180 }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--chart-grid)" vertical={false} />
            <XAxis dataKey="x" tick={{ fontSize: 11, fill: 'var(--chart-tick)' }} tickLine={false} axisLine={{ stroke: 'var(--chart-axis)' }} />
            <YAxis tick={{ fontSize: 11, fill: 'var(--chart-tick)' }} tickLine={false} axisLine={false} width={44}
              tickFormatter={(v) => (format ? format(v) : v >= 1000 ? `${(v / 1000).toFixed(0)}k` : String(v))} />
            <Tooltip
              contentStyle={{ borderRadius: 8, border: '1px solid var(--chart-tooltip-border)', backgroundColor: 'var(--chart-tooltip-bg)', color: 'var(--chart-tooltip-text)', fontSize: 12 }} labelStyle={{ color: 'var(--chart-tooltip-text)', fontWeight: 600 }}
              formatter={(v: number) => [format ? format(v) : v.toLocaleString(), title]}
            />
            <Line name={title} type="monotone" dataKey="y" stroke={color} strokeWidth={2} dot={{ r: 3, fill: color }} activeDot={{ r: 5 }} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </Card>
  );
}
